import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/**
 * A simple map component that displays a location using Google Maps embed
 */
const LocationMap = ({ open, onClose, location }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  // Reset loading state when location changes
  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
    }
  }, [open, location]);

  // Generate a Google Maps embed URL from the location
  const getMapUrl = () => {
    if (!location) return '';
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

    // If we have a Place ID, use place mode (shows a pin)
    if (location.placeData?.placeId) {
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=place_id:${location.placeData.placeId}`;
    }

    // If we have coordinates, use search mode with lat,lng (shows a pin)
    if (location.placeData?.coordinates) {
      const { lat, lng } = location.placeData.coordinates;
      return `https://www.google.com/maps/embed/v1/search?key=${apiKey}&q=${lat},${lng}&zoom=15`;
    }

    // Fall back to address in place mode (shows a pin)
    if (location.address) {
      const encodedAddress = encodeURIComponent(location.address);
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedAddress}`;
    }

    // Last resort: use the name as a place query (shows a pin at best match)
    if (location.name) {
      const encodedName = encodeURIComponent(location.name);
      return `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodedName}`;
    }

    return '';
  };

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setLoading(false);
    setError('Failed to load map. Please check the address and try again.');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: theme.palette.mode === 'dark' ? '#16181a' : '#fff',
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 32px rgba(0,0,0,0.4)' 
            : '0 8px 32px rgba(0, 0, 0, 0.08)',
          backdropFilter: 'blur(8px)'
        }
      }}
    >
      <DialogTitle
        sx={{ 
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
            : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
          color: theme.palette.primary.contrastText,
          position: 'relative',
          pr: 6,
          borderBottom: `2px solid ${theme.palette.primary.main}`,
          fontWeight: 700,
          textShadow: theme.palette.mode === 'dark' 
            ? '0 2px 4px rgba(0,0,0,0.3)' 
            : '0 1px 2px rgba(0,0,0,0.1)'
        }}
      >
        <Typography variant="h6" component="span">
          {location?.name || 'Location Map'}
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: 400, position: 'relative' }}>
        {loading && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              bgcolor: 'rgba(0,0,0,0.03)'
            }}
          >
            <CircularProgress />
          </Box>
        )}
        
        {error ? (
          <Box 
            sx={{ 
              p: 3, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%'
            }}
          >
            <Typography color="error" gutterBottom>
              {error}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Address: {location?.address || 'No address provided'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: '100%', width: '100%' }}>
            <iframe
              title="Location Map"
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              src={getMapUrl()}
              allowFullScreen
              onLoad={handleIframeLoad}
              onError={handleIframeError}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
        <Button 
          color="primary" 
          component="a" 
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location?.address || '')}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Google Maps
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationMap;
